import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { apiClient } from '../../components/api/ApiClient';

function Chat() {
    const { id } = useParams();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    useEffect(() => {
        fetchTicketDetails();
        const interval = setInterval(fetchTicketDetails, 60000);
        return () => clearInterval(interval);
    }, [id]);

    const fetchTicketDetails = async () => {
        try {
            const data = await apiClient(`/internal-network/api/ticket/${id}/`, { method: 'GET' });
            if (data && data.ticket) {
                setTicket(data.ticket);
                if (data.messages && Array.isArray(data.messages)) {
                    setMessages(data.messages);
                }
            } else if (data && data.id) {
                setTicket(data);
                if (data.messages) setMessages(data.messages);
            }
        } catch (error) {
            console.error("Error fetching ticket details:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        try {
            await apiClient('/internal-network/api/ticket/new-message/', {
                method: 'POST',
                data: {
                    ticket: id,
                    message: newMessage
                }
            });
            setNewMessage('');
            fetchTicketDetails();
        } catch (error) {
            console.error("Error sending message:", error);
            alert('خطا در ارسال پیام');
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    if (loading) return <div className='text-dark'>در حال بارگذاری...</div>;
    if (!ticket) return <div className='text-dark'>تیکت یافت نشد.</div>;

    return (
        <div className="content-area ps ps--active-y" style={{ height: 'calc(100vh - 100px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="content-area-header sticky-top">
                <div className="page-header-left hstack gap-4">
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <div className="avatar-image">
                            <div className="avatar-text bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                <i className="feather-user"></i>
                            </div>
                        </div>
                        <div className="d-none d-sm-block">
                            <div className="fw-bold d-flex align-items-center">{ticket.title}</div>
                            <div className="d-flex align-items-center mt-1">
                                <span className={`wd-7 ht-7 rounded-circle opacity-75 me-2 ${ticket.status === 'open' ? 'bg-success' : 'bg-secondary'}`}></span>
                                <span className={`fs-9 text-uppercase fw-bold ${ticket.status === 'open' ? 'text-success' : 'text-muted'}`}>
                                    {ticket.status === 'open' ? 'تیکت باز' : 'تیکت بسته'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="page-header-right ms-auto">
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <div className="d-flex flex-column text-end me-3 d-none d-md-flex align-items-end">
                            <span className="fs-11 text-muted">دپارتمان: {ticket.category === 'technical' ? 'واحد فنی' : ticket.category === 'sale' ? 'واحد فروش' : ticket.category}</span>
                            <span className="fs-10 text-muted">آخرین بروزرسانی: {ticket.last_message_time || '---'}</span>
                        </div>
                        <div className="dropdown">
                            <a href="#" className="avatar-text avatar-md" data-bs-toggle="dropdown" onClick={(e) => e.preventDefault()}>
                                <i className="feather-more-vertical"></i>
                            </a>
                            <div className="dropdown-menu dropdown-menu-end">
                                <a href="#" className="dropdown-item" onClick={(e) => { e.preventDefault(); fetchTicketDetails(); }}>
                                    <i className="feather-refresh-cw me-3"></i>
                                    <span>به‌روزرسانی</span>
                                </a>
                                <a href="#" className="dropdown-item" onClick={(e) => { e.preventDefault(); setShowDetailsModal(true); }}>
                                    <i className="feather-info me-3"></i>
                                    <span>مشخصات تیکت</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages Body */}
            <div className="content-area-body flex-grow-1 p-4" style={{ overflowY: 'auto' }}>
                {messages.length > 0 ? (
                    messages.map((msg, index) => {
                        const isStaff = msg.is_staff_response;
                        return (
                            <div key={index} className="single-chat-item mb-5">
                                <div className={`d-flex ${isStaff ? 'flex-row-reverse' : ''} align-items-center gap-3 mb-3`}>
                                    <div className="avatar-image">
                                        <div className={`avatar-text ${isStaff ? 'bg-info' : 'bg-primary'} text-white rounded-circle d-flex align-items-center justify-content-center`} style={{ width: '35px', height: '35px' }}>
                                            <i className={`feather-${isStaff ? 'headphones' : 'user'}`}></i>
                                        </div>
                                    </div>
                                    <div className={`d-flex ${isStaff ? 'flex-row-reverse' : ''} align-items-center gap-2`}>
                                        <span className="fw-bold fs-12">{msg.sender || (isStaff ? 'پشتیبانی' : 'شما')}</span>
                                        <span className="wd-5 ht-5 bg-gray-400 rounded-circle"></span>
                                        <span className="fs-11 text-muted ms-1" dir="ltr">{msg.created_at || '---'}</span>                                    </div>
                                </div>
                                <div className={`${isStaff ? 'ms-auto' : ''} wd-500 p-3 rounded-5 bg-gray-200`} style={{ maxWidth: '85%' }}>
                                    <div className="py-2 px-3 rounded-5 bg-white shadow-sm">
                                        <p className="mb-0 fs-13" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{msg.message}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center text-muted mt-5">
                        <i className="feather-message-square fs-1 text-gray-400 d-block mb-3"></i>
                        پیامی وجود ندارد.
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Message Editor */}
            <div className="d-flex align-items-center justify-content-between border-top border-gray-5 bg-white sticky-bottom p-2">
                <div className="d-flex align-center">
                </div>
                <div className="flex-grow-1 px-3">
                    <textarea 
                        className="form-control border-0 bg-transparent fs-13" 
                        placeholder="پیام خود را اینجا بنویسید..." 
                        rows="1"
                        style={{ resize: 'none' }}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                    ></textarea>
                </div>
                <div className="pe-2">
                    <button 
                        className="btn btn-primary btn-icon rounded-circle wd-40 ht-40 d-flex align-items-center justify-content-center shadow-sm"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim()}
                    >
                        <i className="feather-send fs-16"></i>
                    </button>
                </div>
            </div>
            <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>مشخصات تیکت</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-4">
                        <div className="col-md-12">
                            <label className="text-muted mb-1 fs-12">عنوان تیکت:</label>
                            <div className="fw-bold text-dark">{ticket.title}</div>
                        </div>
                        <div className="col-md-6">
                            <label className="text-muted mb-1 fs-12">شماره تیکت:</label>
                            <div className="fw-bold text-dark">{ticket.id}</div>
                        </div>
                        <div className="col-md-6">
                            <label className="text-muted mb-1 fs-12">وضعیت:</label>
                            <div>
                                <span className={`badge ${ticket.status === 'open' ? 'bg-success' : 'bg-secondary'}`}>
                                    {ticket.status === 'open' ? 'باز' : 'بسته'}
                                </span>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label className="text-muted mb-1 fs-12">دپارتمان:</label>
                            <div className="fw-bold text-dark">
                                {ticket.category === 'technical' ? 'واحد فنی' : ticket.category === 'sale' ? 'واحد فروش' : ticket.category}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label className="text-muted mb-1 fs-12">اولویت:</label>
                            <div className="fw-bold text-dark">
                                {ticket.priority === 'high' ? 'بالا' : ticket.priority === 'medium' ? 'متوسط' : 'پایین'}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label className="text-muted mb-1 fs-12">تاریخ ایجاد:</label>
                            <div className="fw-bold text-dark" dir="ltr">{ticket.created_at || '---'}</div>
                        </div>
                        <div className="col-md-6">
                            <label className="text-muted mb-1 fs-12">آخرین بروزرسانی:</label>
                            <div className="fw-bold text-dark" dir="ltr">{ticket.last_message_time || '---'}</div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                        بستن
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default Chat;